import { z } from 'zod';
import { StreamModeSchema } from './modes.js';
export const IdSchema = z.string().min(1).max(64);
export const SUPPORT_SESSION_STATUSES = [
    'waiting_customer',
    'customer_working',
    'customer_done',
    'operator_working',
    'completed',
];
export const SupportSessionStatusSchema = z.enum(SUPPORT_SESSION_STATUSES);
/** Chrome process state for a browser profile, tracked in memory by server and node. */
export const BROWSER_RUNTIMES = ['closed', 'launching', 'open', 'error'];
export const BrowserRuntimeSchema = z.enum(BROWSER_RUNTIMES);
export const USER_ROLES = ['admin', 'member'];
export const UserRoleSchema = z.enum(USER_ROLES);
export const PARTICIPANT_KINDS = ['operator', 'customer'];
export const ParticipantKindSchema = z.enum(PARTICIPANT_KINDS);
export const WindowSizeSchema = z.object({
    width: z.number().int().min(320).max(3840),
    height: z.number().int().min(240).max(2160),
});
export const DEFAULT_WINDOW_SIZE = { width: 1280, height: 800 };
export const WINDOW_PRESETS = [
    { label: 'Desktop 1280×800', width: 1280, height: 800 },
    { label: 'Desktop 1440×900', width: 1440, height: 900 },
    { label: 'Desktop 1920×1080', width: 1920, height: 1080 },
    { label: 'Narrow 1024×768', width: 1024, height: 768 },
    { label: 'Mobile-ish 480×860', width: 480, height: 860 },
];
/**
 * Region presented by the browser. Both are optional; when a profile uses a proxy they should match
 * the proxy's exit region, because a Korean locale/time zone behind a US IP is itself a signal.
 */
export const LocaleSchema = z
    .string()
    .max(35)
    .regex(/^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{2,8})*$/, 'BCP 47 형식이어야 합니다 (예: ko-KR, en-US)');
export const TimezoneSchema = z
    .string()
    .max(64)
    .regex(/^[A-Za-z_]+(\/[A-Za-z0-9_+-]+)*$/, 'IANA 시간대여야 합니다 (예: Asia/Seoul)')
    .refine((tz) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, { message: '알 수 없는 시간대입니다' });
/** Proxy config as sent to the node (password in clear, only over the authenticated node socket). */
export const ProxyConfigSchema = z.object({
    /** e.g. http://host:port or socks5://host:port (SOCKS5 with auth is NOT supported by Chrome) */
    server: z.string().min(1).max(512),
    username: z.string().max(256).optional(),
    password: z.string().max(256).optional(),
    /** Comma-separated bypass list, e.g. "localhost,*.internal" */
    bypass: z.string().max(1024).optional(),
});
export const TabInfoSchema = z.object({
    tabId: IdSchema,
    url: z.string(),
    title: z.string(),
    active: z.boolean(),
    /** Set when the tab was opened by another page (popup) */
    openerTabId: IdSchema.optional(),
});
export const ParticipantSchema = z.object({
    participantId: IdSchema,
    kind: ParticipantKindSchema,
    /** operator display name or customer label */
    name: z.string(),
    /** operator user id; absent for customers */
    userId: IdSchema.optional(),
    /** which tab this participant is currently viewing */
    tabId: IdSchema.optional(),
    mode: StreamModeSchema.optional(),
    joinedAt: z.number(),
});
export const ErrorInfoSchema = z.object({
    code: z.string(),
    message: z.string(),
});
// ---------------------------------------------------------------------------
// Input events (viewer -> server -> node). Coordinates are CSS pixels relative to
// the page viewport, i.e. already mapped by the viewer using frame metadata.
// ---------------------------------------------------------------------------
/** CDP modifier bitmask: Alt=1, Ctrl=2, Meta=4, Shift=8 */
export const ModifiersSchema = z.number().int().min(0).max(15);
export const MouseButtonSchema = z.enum(['none', 'left', 'middle', 'right', 'back', 'forward']);
export const InputMouseSchema = z.object({
    t: z.literal('input.mouse'),
    tabId: IdSchema,
    type: z.enum(['mousePressed', 'mouseReleased', 'mouseMoved']),
    x: z.number(),
    y: z.number(),
    button: MouseButtonSchema.default('none'),
    /** bitmask: left=1, right=2, middle=4, back=8, forward=16 */
    buttons: z.number().int().min(0).max(31).default(0),
    clickCount: z.number().int().min(0).max(3).default(0),
    modifiers: ModifiersSchema.default(0),
});
export const InputWheelSchema = z.object({
    t: z.literal('input.wheel'),
    tabId: IdSchema,
    x: z.number(),
    y: z.number(),
    deltaX: z.number(),
    deltaY: z.number(),
    modifiers: ModifiersSchema.default(0),
});
export const InputKeySchema = z.object({
    t: z.literal('input.key'),
    tabId: IdSchema,
    type: z.enum(['keyDown', 'keyUp', 'rawKeyDown']),
    key: z.string().max(32),
    code: z.string().max(32),
    windowsVirtualKeyCode: z.number().int().min(0).max(255),
    /** Printable text for keyDown (generates keypress). Omit for control keys. */
    text: z.string().max(16).optional(),
    modifiers: ModifiersSchema.default(0),
    /** Repeat flag from the viewer's keydown */
    autoRepeat: z.boolean().default(false),
});
/** IME composition in progress (Korean/Japanese/...); maps to Input.imeSetComposition */
export const InputImeSchema = z.object({
    t: z.literal('input.ime'),
    tabId: IdSchema,
    text: z.string().max(256),
    selectionStart: z.number().int().min(0),
    selectionEnd: z.number().int().min(0),
});
/** Committed text (IME compositionend, paste); maps to Input.insertText. Empty string cancels composition. */
export const InputTextSchema = z.object({
    t: z.literal('input.text'),
    tabId: IdSchema,
    text: z.string().max(65536),
});
export const InputEventSchema = z.discriminatedUnion('t', [
    InputMouseSchema,
    InputWheelSchema,
    InputKeySchema,
    InputImeSchema,
    InputTextSchema,
]);
export const INPUT_EVENT_TYPES = [
    'input.mouse',
    'input.wheel',
    'input.key',
    'input.ime',
    'input.text',
];
export function isInputEvent(t) {
    return INPUT_EVENT_TYPES.includes(t);
}
//# sourceMappingURL=common.js.map